<template>
    <div class="progress-row">
        <div class="progress-container"
            ref="container"
            @pointerdown="$emit('pointerdown', $event)"
            @pointermove="$emit('pointermove', $event)"
            @pointerup="$emit('pointerup', $event)"
            @pointercancel="$emit('pointercancel', $event)"
            @pointerleave="$emit('pointerleave', $event)"
        >
            <div class="progress-bar">
                <div class="progress-buffered" :style="{ width: bufferedPercent + '%' }"></div>
                <div class="progress-filled" :style="{ width: progressPercent + '%' }"></div>
                <div class="progress-handle" :style="{ left: progressPercent + '%' }"></div>
            </div>
            <div class="time-tooltip" :class="{ 'has-preview': previewStyle }" v-if="showTimeTooltip" :style="tooltipPosition()">
                <div class="preview-frame" v-if="previewStyle" :style="previewStyle"></div>
                <span>{{ hoverTimeFormatted }}</span>
            </div>
        </div>

        <div class="time-display">{{ durationFormatted }}</div>
    </div>
</template>

<script>
export default {
    name: 'ProgressBar',
    props: {
        bufferedPercent: { type: Number, default: 0 },
        progressPercent: { type: Number, default: 0 },
        durationFormatted: { type: String, default: '0:00' },
        hoverTimeFormatted: { type: String, default: '0:00' },
        showTimeTooltip: { type: Boolean, default: false },
        hoverPercent: { type: Number, default: 0 },
        hoverTime: { type: Number, default: 0 },
        // { base, interval, cols, rows, width, height, count } from /transcode-video
        thumbnails: { type: Object, default: null },
    },
    data() {
        return {
            // sprite sheet index -> 'loading' | 'ready' | 'missing'
            sheetStatus: {},
        }
    },
    created() {
        // not reactive on purpose: only used to throttle retries of sheets still being generated
        this.sheetFailedAt = {}
    },
    computed: {
        previewIndex() {
            const t = this.thumbnails
            if (!t || !t.interval || !isFinite(this.hoverTime)) return -1
            return Math.min(t.count - 1, Math.max(0, Math.floor(this.hoverTime / t.interval)))
        },
        previewStyle() {
            const t = this.thumbnails
            if (!t || this.previewIndex < 0) return null
            const perSheet = t.cols * t.rows
            const sheet = Math.floor(this.previewIndex / perSheet)
            if (this.sheetStatus[sheet] !== 'ready') return null
            const pos = this.previewIndex % perSheet
            const col = pos % t.cols
            const row = Math.floor(pos / t.cols)
            return {
                width: t.width + 'px',
                height: t.height + 'px',
                backgroundImage: `url(${this.sheetUrl(sheet)})`,
                backgroundSize: `${t.cols * t.width}px ${t.rows * t.height}px`,
                backgroundPosition: `-${col * t.width}px -${row * t.height}px`,
                transitionDuration: '0s !important'
            }
        },
    },
    watch: {
        thumbnails() {
            this.sheetStatus = {}
            this.sheetFailedAt = {}
        },
        previewIndex() { this.ensureSheet() },
        showTimeTooltip(show) { if (show) this.ensureSheet() },
    },
    methods: {
        sheetUrl(sheet) {
            return `${this.thumbnails.base}/sheet_${String(sheet).padStart(3, '0')}.jpg`
        },
        // Load the sheet the hovered frame lives in. Sheets are generated in the
        // background, so one that 404s is retried (at most every 3s) while hovering.
        ensureSheet() {
            const t = this.thumbnails
            if (!t || this.previewIndex < 0) return
            const sheet = Math.floor(this.previewIndex / (t.cols * t.rows))
            const status = this.sheetStatus[sheet]
            if (status === 'ready' || status === 'loading') return
            if (status === 'missing' && Date.now() - (this.sheetFailedAt[sheet] || 0) < 3000) return

            const base = t.base
            const settle = (state) => {
                if (!this.thumbnails || this.thumbnails.base !== base) return
                if (state === 'missing') this.sheetFailedAt[sheet] = Date.now()
                this.$set(this.sheetStatus, sheet, state)
            }
            this.$set(this.sheetStatus, sheet, 'loading')
            const img = new Image()
            img.onload = () => settle('ready')
            img.onerror = () => settle('missing')
            img.src = this.sheetUrl(sheet)
        },
        // Keeps the wider preview tooltip inside the bar instead of spilling off either edge.
        tooltipPosition() {
            const left = this.hoverPercent + '%'
            if (!this.previewStyle || !this.$refs.container) return { left }
            const width = this.$refs.container.clientWidth
            const half = this.thumbnails.width / 2 + 4 // frame + tooltip padding
            const x = (this.hoverPercent / 100) * width
            return { left: Math.min(Math.max(x, half), width - half) + 'px' }
        },
    },
}
</script>

<style lang="scss" scoped>
.progress-row {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    padding: 10px 0 5px;
}

.progress-container {
    flex: 1;
    min-width: 0;
    padding: 10px 0;
    cursor: pointer;
    position: relative;
    touch-action: none;
    -webkit-tap-highlight-color: transparent;

    .progress-bar {
        width: 100%;
        height: 4px;
        background: rgba(255, 255, 255, 0.25);
        border-radius: 2px;
        position: relative;
        transition: height 0.15s;
    }

    &:hover .progress-bar {
        height: 6px;
    }

    &:hover .progress-handle {
        transform: translate(-50%, -50%) scale(1.15);
    }

    .progress-buffered {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        background: rgba(255, 255, 255, 0.45);
        border-radius: 2px;
        pointer-events: none;
        transition: width 0.2s linear;
    }

    .progress-filled {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        background: #e50914;
        border-radius: 2px;
        pointer-events: none;
    }

    .progress-handle {
        position: absolute;
        top: 50%;
        left: 0;
        transform: translate(-50%, -50%);
        width: 14px;
        height: 14px;
        background: #e50914;
        border-radius: 50%;
        pointer-events: none;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
        transition: transform 0.15s ease;
    }

    .time-tooltip {
        position: absolute;
        bottom: 100%;
        transform: translateX(-50%);
        margin-bottom: 8px;
        background: rgba(0, 0, 0, 0.9);
        color: white;
        padding: 5px 10px;
        border-radius: 4px;
        font-size: 12px;
        white-space: nowrap;
        pointer-events: none;
        z-index: 10;
        transition: none;


        &.has-preview {
            padding: 4px;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
        }

        .preview-frame {
            border-radius: 2px;
            transition: none;
            background-color: #000;
            background-repeat: no-repeat;
        }
    }
}

.time-display {
    flex-shrink: 0;
    color: white;
    font-size: 15px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}
</style>
